import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { Link, Redirect } from 'react-router-dom';
import { connect } from 'react-redux';
import {
  Button,
  Form,
  FormGroup,
  Input,
  Label,
} from 'reactstrap';
import { injectIntl } from 'react-intl';

import { setLoggedIn } from '../actions/App';
import {
  login,
  clearUserProfile,
} from '../actions/Auth';

import {
  getUrlParam,
  hideZendeskWebWidget,
} from '../helpers';
import { cleanRedirectUrl } from '../helpers/App';
import { getSetting } from '../helpers/Settings';

import InputPassword from '../components/InputPassword';
import LoginGoogleButton from '../components/LoginGoogleButton';
import LoginSSOButton from '../components/LoginSSOButton';
import Loading from '../components/Loading';

class Login extends PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      email: '',
      password: '',
      loginToken: '',
      jwtToken: '',
      googleCredential: '',
      g2faCode: '',
      codeChallenge: '',
      redirectUri: '',
      clientId: null,
      step: 'login',
      redirect: false,
      error: null,
      loading: false,
    };

    this.handleChange = this.handleChange.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleGoogleSignIn = this.handleGoogleSignIn.bind(this);
    this.backToLogin = this.backToLogin.bind(this);
  }

  componentDidMount() {
    hideZendeskWebWidget();
    this.checkLoginToken();
  }

  checkLoginToken() {
    if (getUrlParam('login_token')) {
      this.setState({ loginToken: getUrlParam('login_token') }, () => {
        this.handleSubmit();
      });
    }

    if (getUrlParam('jwt_token')) {
      this.setState({ jwtToken: getUrlParam('jwt_token') }, () => {
        this.handleSubmit();
      });
    }

    if (getUrlParam('code_challenge')) {
      this.setState({ codeChallenge: getUrlParam('code_challenge') });
    }

    if (getUrlParam('redirect_uri')) {
      this.setState({ redirectUri: getUrlParam('redirect_uri') });
    }

    if (getUrlParam('client_id')) {
      this.setState({ clientId: getUrlParam('client_id') });
    }

    if (getUrlParam('state')) {
      this.setState({ state: getUrlParam('state') });
    }
  }

  handleChange(e) {
    const { state } = this;
    state[e.target.name] = e.target.value;
    this.setState(state);
  }

  handleSubmit(e) {
    if (e) {
      e.preventDefault();
    }

    this.setState({
      loading: true,
      error: null,
    });

    if (!this.isFormValid()) {
      this.setState({ loading: false });
      return;
    }

    const {
      intl,
      dispatch,
    } = this.props;
    const {
      email,
      password,
      loginToken,
      jwtToken,
      googleCredential,
      g2faCode,
      redirectUri,
      codeChallenge,
      clientId,
      state,
    } = this.state;

    dispatch(login({
      username: email,
      password,
      loginToken,
      jwtToken,
      googleCredential,
      g2faCode,
      codeChallenge,
      redirectUri,
      clientId,
      state,
      clearAuthDataOnError: false,
    })).then((response) => {
      if (parseInt(response.status) === 200) {
        dispatch(setLoggedIn(true));
        dispatch(clearUserProfile());
        if (response.data.redirect_url) {
          window.location.href = `${response.data.redirect_url}`;
        }
        this.setState({ redirect: true });
      } else if (response.data.error === '2fa_code_required') {
        this.setState({
          step: '2fa',
          loading: false,
        });
      } else {
        let errorMessage = intl.formatMessage({ defaultMessage: 'Invalid login credentials' });
        if (response.data && response.data.message) {
          errorMessage = response.data.message;
        }
        this.setState({
          error: errorMessage,
          loading: false,
        });
      }
    });
  }

  handleGoogleSignIn(credential) {
    this.setState({ googleCredential: credential }, () => {
      this.handleSubmit();
    });
  }

  backToLogin(e) {
    e.preventDefault();
    this.setState({
      email: '',
      password: '',
      g2faCode: '',
      step: 'login',
      error: null,
    });
  }

  isFormValid() {
    const { intl } = this.props;
    const {
      email,
      password,
      loginToken,
      jwtToken,
      googleCredential,
      step,
      g2faCode,
    } = this.state;

    if (!loginToken && !jwtToken && !googleCredential && (!email || !password)) {
      this.setState({ error: intl.formatMessage({ defaultMessage: 'Please enter your email address and password' }) });
      return false;
    }

    if (step === '2fa' && !g2faCode) {
      this.setState({ error: intl.formatMessage({ defaultMessage: 'Please enter your 2FA code' }) });
      return false;
    }

    return true;
  }

  render() {
    const {
      loginToken,
      jwtToken,
      googleCredential,
      error,
      step,
      redirect,
      loading,
    } = this.state;
    const {
      intl,
      location,
      profile,
      profileLoading,
    } = this.props;

    if (redirect && profile) {
      const params = (new URL(document.location)).searchParams;
      const fromUrl = profile.landing_url ? `${profile.landing_url}?${params.toString()}` : `/?${params.toString()}`;
      const { from } = location.state || { from: fromUrl };
      return (
        <Redirect to={cleanRedirectUrl(from)} />
      );
    }

    const forceSSO = profile?.client?.force_sso;
    const googleLoginButtonPosition = (profile && getSetting('google_login_enabled') && getSetting('google_login_position')) ?
      getSetting('google_login_position') :
      null;

    return (
      <div className="layout layout-login">
        <div className="login-wrapper">
          <div className="login-inner">
            <div className="login-logo">
              {profile?.client ? (
                <img src={profile.client.login_logo_url} alt="" />
              ) : (
                <>
                  {!profileLoading && (
                    <img src="/logo/login" alt="" />
                  )}
                </>
              )}
            </div>
            {((loginToken || jwtToken || googleCredential) && loading) ? (
              <>
                <div className="login-message">
                  <h4>{intl.formatMessage({ defaultMessage: 'Logging in...' })}</h4>
                </div>
                <Loading />
              </>
            ) : (
              <Form
                className="login-form"
                onChange={this.handleChange}
                onSubmit={this.handleSubmit}
              >
                {(profile?.client?.saml_login_url && getSetting('saml_login_button_position') === 'top') && (
                  getSetting('cs1') ? (
                    <div className="login-saml">
                      <a href={profile.client.saml_login_url} title="Login with MyID" tabIndex="-1">
                        {intl.formatMessage({ defaultMessage: 'Login with' })}{' '}
                        <img src="/images/espn/myid.svg" alt={intl.formatMessage({ defaultMessage: 'Login with MyID' })} />
                      </a>
                    </div>
                  ) : (
                    <LoginSSOButton profile={profile} />
                  )
                )}
                {googleLoginButtonPosition === 'top' && (
                  <LoginGoogleButton handleGoogleSignIn={this.handleGoogleSignIn} />
                )}
                {error && (
                  <div className="error">
                    {error}
                  </div>
                )}
                {!profile && (
                  <Loading />
                )}
                {(profile && step === 'login' && !forceSSO) && (
                  <>
                    <div className="groups-wrapper">
                      <FormGroup>
                        <Input
                          type="email"
                          name="email"
                          id="email"
                          placeholder={intl.formatMessage({ defaultMessage: 'Email' })}
                          disabled={loading}
                          autoFocus
                        />
                      </FormGroup>
                      <InputPassword
                        name="password"
                        disabled={loading}
                      />
                    </div>
                    <div className="login-forgot">
                      <Link to="/password-reset">
                        {intl.formatMessage({ defaultMessage: 'Forgot your password?' })}
                      </Link>
                    </div>
                  </>
                )}
                {(profile && step === '2fa') && (
                  <>
                    <div className="groups-wrapper g2fa-group">
                      <FormGroup>
                        <Label htmlFor="g2fa_code">{intl.formatMessage({ defaultMessage: 'Enter a token from your 2FA app' })}</Label>
                        <Input
                          type="text"
                          name="g2faCode"
                          id="g2fa_code"
                          placeholder={intl.formatMessage({ defaultMessage: '6-digit token' })}
                          maxLength="12"
                          autoFocus
                        />
                      </FormGroup>
                    </div>
                    <div className="login-forgot">
                      <a href="#back" onClick={this.backToLogin}>
                        {intl.formatMessage({ defaultMessage: 'Back to Login' })}
                      </a>
                    </div>
                  </>
                )}
                {(profile && !forceSSO) && (
                  <div className="btn-wrapper">
                    <Button
                      type="submit"
                      disabled={loading}
                      color="primary"
                    >
                      {loading ? intl.formatMessage({ defaultMessage: 'Logging in...' }) :
                        intl.formatMessage({ defaultMessage: 'Login' })}
                    </Button>
                  </div>
                )}
                {(profile?.client?.saml_login_url && getSetting('saml_login_button_position') === 'bottom') && (
                  getSetting('cs1') ? (
                    <div className="login-saml">
                      <a href={profile.client.saml_login_url} title="Login with MyID" tabIndex="-1">
                        {intl.formatMessage({ defaultMessage: 'Login with' })}{' '}
                        <img src="/images/espn/myid.svg" alt={intl.formatMessage({ defaultMessage: 'Login with MyID' })} />
                      </a>
                    </div>
                  ) : (
                    <LoginSSOButton profile={profile} />
                  )
                )}
                {googleLoginButtonPosition === 'bottom' && (
                  <LoginGoogleButton handleGoogleSignIn={this.handleGoogleSignIn} />
                )}
                {(profile && getSetting('allow_user_registration')) && (
                  <div className="register-link">
                    <Button outline tag={Link} color="primary" to="/register" tabIndex="-1">
                      {getSetting('user_registration_link_text')}
                    </Button>
                  </div>
                )}
              </Form>
            )}
          </div>
        </div>
      </div>
    );
  }
}

const mapStateToProps = state => ({
  profile: state.Auth.profile,
  profileLoading: state.Auth.loading,
});

Login.propTypes = {
  intl: PropTypes.object.isRequired,
  dispatch: PropTypes.func.isRequired,
  profile: PropTypes.object,
  profileLoading: PropTypes.bool.isRequired,
  location: PropTypes.object,
};

Login.defaultProps = {
  profile: null,
  location: null,
};

export default injectIntl(connect(mapStateToProps)(Login));
